import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage} from 'react-intl';

import {IStore} from 'redux/interface';
import {dialogueAsyncActions, startXpedition, getTranslations, restartXpedition} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {BotType} from 'utils/hooks/use-chat-bot/interfaces';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import BotChat from 'components/bot-chat';

import {closeArticle} from 'redux/content/actions';
import XpeditionIcon from 'assets/image/menu-auth/xpedition.png';
import {dialogueClearedAction} from 'redux/dialogue/actions';

import './styles.scss';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {BotMode} from '@wholesalechange/chatcomponent';

export const XpeditionChat: React.FunctionComponent = () => {
  const {
    dialogue
  } = useSelector((state: IStore) => state.dialogue);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const [dialogueId, setDialogueId] = useState<number>();
  const {xpeditionId, restart} = useParams();
  const [searchParams] = useSearchParams();
  const apIdParam = searchParams.get('ap_id');
  const apId = apIdParam ? Number(apIdParam) : undefined;
  const history = useNavigate();

  useEffect(() => {
    dispatch(closeArticle());
  }, []);

  useEffect(() => {
    if (dialogue) {
      setDialogueId(dialogue.id);
    }
  }, [dialogue]);

  useEffect(() => {
    if (xpeditionId) {
      // A stale dialogueId (and dialogue) from a previously-opened xpedition session must not
      // stay visible while the new session loads -- BotChat renders immediately off dialogueId,
      // so without this reset it briefly shows the PREVIOUS session's content until the fetch
      // below resolves. Mirrors the dialogueClearedAction() dispatch already used by every
      // sibling chat page (Xpedition, ActivityChat, ActivityJournalChat, Chatroom, Dialogue).
      setDialogueId(undefined);
      dispatch(dialogueClearedAction());
      if (restart === 'restart') {
        dispatch(dialogueAsyncActions[restartXpedition](Number(xpeditionId), apId));
        history(`/xpedition/${xpeditionId}${apIdParam ? `?ap_id=${apIdParam}` : ''}`, {replace: true});
      } else {
        dispatch(dialogueAsyncActions[startXpedition](Number(xpeditionId), apId));
      }
    }
  }, [xpeditionId]);

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Xpedition.toLowerCase());

    return page?.translationKey || TranslationKeys.Chat;
  }, [settings]);

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Xpedition.toLowerCase());

    return page?.title || Pages.Xpedition;
  }, [settings]);

  useGetTranslations({translationKey});

  return <div className="xpedition">
    {
      !loading.includes(getTranslations) && <>
        <h1 className="xpedition__title">
          <img src={XpeditionIcon} alt="" className="xpedition__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          /> {dialogue && <span className="xpedition__title-dialogue-name"> - {dialogue.name}</span>}
        </h1>
        <BotChat chatType={BotType.Xpedition} chatMode={BotMode.NoHistory} dialogueId={dialogueId}
          apId={apId}
          title={dialogue?.name || ''}
          tabsEnabled={settings?.chatSettings?.tabsEnabled || false}/>
      </>
    }
  </div>;
};
